import { PlannerConfigEntity } from '@/services/configurator/PlannerConfigTypes';
import { Staging } from '@/services/configurator/Staging';
import { ConfiguratorApi } from '@/utils/ConfiguratorApi';
import { toast } from 'sonner';
import { PlannerPublisStagingList } from '../planner-publish-list';
import { Release } from '@/services/configurator/Release';
import { PlannerEnv } from '@/utils/env';
import { QueryClient } from '@tanstack/react-query';
type Role = 'admin' | 'creator' | 'editor' | 'viewer';

export const getPlannerStagingData = async (
  env: PlannerEnv,
  diffPlannerId: string,
) => {
  return new Staging(env).getPlannerData(diffPlannerId);
};

export const getPlannerProductionData = async (
  identifier: string,
  env: PlannerEnv,
) => {
  return ConfiguratorApi.getInstance().getPublishedPlanner(env, identifier);
};

export const getStagingDataList = async (env: PlannerEnv) => {
  return new Staging(env).getList().then((stagingPlanners) => stagingPlanners);
};

export const fetchAndUpdateList = async (
  env: PlannerEnv,
  role: Role,
  userEmail: string,
): Promise<PlannerPublisStagingList[]> => {
  return new Staging(env).getList().then((data) => {
    const reducedList = data.reduce((acc, item) => {
      if (item.id && item.lastModified) {
        acc.push({
          id: item.id,
          value: item.identifier,
          date: item.lastModified,
          modifiedBy: item.modifiedBy,
        });
      }
      return acc;
    }, [] as PlannerPublisStagingList[]);

    let list = [];

    if (role === 'admin') {
      list = reducedList;
    } else {
      list = reducedList.filter((item) => item.modifiedBy === userEmail);
    }

    list.sort((a, b) => {
      return new Date(a.date).valueOf() - new Date(b.date).valueOf();
    });

    return list;
  });
};

export const updateListAndInvalidate = async (
  queryClient: QueryClient,
  env: PlannerEnv,
  role: Role,
  userEmail: string,
) => {
  const list = await fetchAndUpdateList(env, role, userEmail);
  await queryClient.invalidateQueries({
    queryKey: ['get-planners'],
  });
  return list;
};

export const deleteAllSelected = async (
  allSelected: string[],
  env: PlannerEnv,
) => {
  if (allSelected.length === 0) {
    toast.warning('No planners selected to delete');
    return;
  }
  await Promise.all(
    allSelected.map(async (plannerId) => {
      await new Staging(env).deletePlanner(plannerId);
    }),
  );
};

export const publishAllSelected = async (
  allSelected: string[],
  publishToEnv: PlannerEnv,
) => {
  if (allSelected.length === 0) {
    toast.warning('No planners selected to publish');
    return;
  }

  const publishPromises: Promise<unknown>[] = [];
  allSelected.forEach((id) => {
    publishPromises.push(new Release(publishToEnv).release(id));
  });

  await Promise.all(publishPromises);
};

export const getPlannerData = (
  id: string,
  stagingData: PlannerConfigEntity[] | undefined,
): PlannerConfigEntity =>
  stagingData?.length
    ? (stagingData.find((planner) => planner.id === id) ??
      ({} as PlannerConfigEntity))
    : ({} as PlannerConfigEntity);
